import inspect from 'browser-util-inspect';
import { BehaviorSubject, Observable } from 'rxjs';
import { asString } from '../util/misc.js';
import { wrapObs } from '../util/rx.js';
import { delay, timeoutRace, wrapSettlement } from '../util/sched.js';
import { BleDeviceError, } from './interface.js';
import { pupAppVersionChar, pupService, silabsOtaAppVersionChar, silabsOtaService, } from './uuids.js';
const pathErrorMap = new Map([
    [/^Bluetooth is OFF$/, { cause: 'bleDisabled' }],
    [
        /^Service (.*) not known on device$/,
        { cause: 'charNotFound', makeInfo: (arr) => `svc ${arr[1]}` },
    ],
    [/^No services found$/, { cause: 'charNotFound' }],
    [
        /^Characteristic (.*) not known for service (.*) on device$/,
        { cause: 'charNotFound', makeInfo: (arr) => `svc ${arr[2]} char ${arr[1]}` },
    ],
    [/^Device disconnected/, { cause: 'notConnected' }],
    [/^Characteristics have not yet been retrieved for service/, { cause: 'charNotFound' }],
    [/^An error occurred discovering services for the device/, { cause: 'charNotFound' }],
    [/^Error reading characteristic/, { cause: 'gatt' }],
    [/^Error writing characteristic/, { cause: 'gatt' }],
    [/^Characteristic does not support writing/, { cause: 'gatt' }],
]);
class UnexpectedError extends Error {
    err;
    constructor(err) {
        super(inspect(err));
        this.err = err;
    }
}
const translateError = (err) => {
    if (err instanceof DOMException) {
        switch (err.name) {
            case 'NotFoundError':
                return new BleDeviceError('charNotFound');
            case 'NetworkError':
                return new BleDeviceError('gatt');
            case 'NotSupportedError':
                return new BleDeviceError('gatt');
        }
    }
    if (typeof err === 'string') {
        for (const [regex, trans] of pathErrorMap.entries()) {
            const match = regex.exec(err);
            if (match) {
                const info = trans.makeInfo?.(match);
                return new BleDeviceError(trans.cause, info);
            }
        }
    }
    if (!(err instanceof Error)) {
        return new UnexpectedError(err);
    }
    return err;
};
const defaultOptions = {
    wrapper: (inner) => inner(),
    timeout: 3000,
};
/** Implementation of `ConnectedDevice` for Web Bluetooth */
export class WebBluetoothConnectedDevice {
    wbDev;
    logger;
    wrapper;
    /**
     * Create from a `BluetoothDevice`.
     *
     * @param dev `BluetoothDevice` to wrap
     * @param logger `Logger` to use
     * @param options Options
     */
    static async create(dev, logger, options = {}) {
        const { wrapper, timeout } = { ...defaultOptions, ...options };
        const connDev = new WebBluetoothConnectedDevice(dev, logger, wrapper);
        const onGattserverdisconnected = (ev) => {
            dev.removeEventListener('gattserverdisconnected', onGattserverdisconnected);
            if (connDev.state === 'disconnecting') {
                logger.log('Successfully disconnected from GATT server', ev);
            }
            else {
                logger.warn('GATT server dropped connection', ev);
            }
            connDev.stateSubj.next('disconnected');
        };
        dev.addEventListener('gattserverdisconnected', onGattserverdisconnected);
        try {
            if (timeout) {
                await timeoutRace(connDev.startGatt(), timeout, new BleDeviceError('timedOut'));
            }
            else {
                await connDev.startGatt();
            }
        }
        catch (error) {
            logger.error('Device connection failed.', {
                name: dev.name ? dev.name : dev.id,
                error: asString(error),
            });
            dev.gatt?.disconnect();
            dev.removeEventListener('gattserverdisconnected', onGattserverdisconnected);
            throw translateError(error);
        }
        return connDev;
    }
    stateSubj = new BehaviorSubject('connected');
    svcs = new Map();
    constructor(wbDev, logger, wrapper) {
        this.wbDev = wbDev;
        this.logger = logger;
        this.wrapper = wrapper;
    }
    get state() {
        return this.stateSubj.value;
    }
    get stateObs() {
        return this.stateSubj.asObservable();
    }
    get id() {
        return this.wbDev.id;
    }
    get name() {
        return this.wbDev.name;
    }
    startDisconnect() {
        if (this.stateSubj.value === 'connected') {
            this.stateSubj.next('disconnecting');
            this.wbDev.gatt?.disconnect();
        }
        return Promise.resolve();
    }
    hasChar(svc, char) {
        return !!this.svcs.get(svc)?.chars.get(char);
    }
    async readChar(svc, char) {
        const gattChar = this.getChar(svc, char);
        // Path Browser returns an `ArrayBuffer` instead of a `DataView`
        try {
            const value = await this.wrapSettlement(gattChar.readValue());
            return 'buffer' in value ? value.buffer : value;
        }
        catch (err) {
            throw translateError(err);
        }
    }
    canWriteWithoutResponse(svc, char) {
        const gattChar = this.getChar(svc, char);
        return Promise.resolve(!!gattChar.writeValueWithoutResponse);
    }
    async writeCharWithResponse(svc, char, value) {
        const gattChar = this.getChar(svc, char);
        try {
            if (gattChar.writeValueWithResponse) {
                await this.wrapSettlement(gattChar.writeValueWithResponse('buffer' in value ? value.buffer : value));
            }
            else {
                await this.wrapSettlement(gattChar.writeValue('buffer' in value ? value.buffer : value));
            }
        }
        catch (err) {
            throw translateError(err);
        }
    }
    async writeCharWithoutResponse(svc, char, value) {
        const gattChar = this.getChar(svc, char);
        try {
            await this.wrapSettlement(gattChar.writeValueWithoutResponse('buffer' in value ? value.buffer : value));
        }
        catch (err) {
            throw translateError(err);
        }
    }
    async startNotifications(svc, char) {
        const gattChar = this.getChar(svc, char);
        try {
            await this.wrapSettlement(gattChar.startNotifications());
        }
        catch (err) {
            throw translateError(err);
        }
        return new Observable((subscriber) => {
            const onValue = () => {
                if (gattChar.value) {
                    subscriber.next(gattChar.value.buffer);
                }
            };
            const onDisconnect = () => subscriber.complete();
            gattChar.addEventListener('characteristicvaluechanged', onValue);
            this.wbDev.addEventListener('gattserverdisconnected', onDisconnect);
            return () => {
                gattChar.removeEventListener('characteristicvaluechanged', onValue);
                this.wbDev.removeEventListener('gattserverdisconnected', onDisconnect);
            };
        }).pipe(wrapObs(this.wrapper));
    }
    async stopNotifications(svc, char) {
        const gattChar = this.getChar(svc, char);
        try {
            await this.wrapSettlement(gattChar.stopNotifications());
        }
        catch (err) {
            throw translateError(err);
        }
    }
    /**
     * @brief best-effort read a characteristic to trigger bonding
     * @param timeoutMsec Time to keep trying, in milliseconds
     * @param periodMsec Time to wait between attempts, in milliseconds
     */
    async triggerBonding(timeoutMsec = 30000, periodMsec = 500) {
        for (const [svcUuid, charUuid] of [
            [silabsOtaService, silabsOtaAppVersionChar],
            [pupService, pupAppVersionChar],
        ]) {
            const char = this.svcs.get(svcUuid)?.chars.get(charUuid);
            if (char) {
                const endTime = performance.now() + timeoutMsec;
                for (;;) {
                    try {
                        await this.wrapSettlement(char.readValue());
                        break;
                    }
                    catch (err) {
                        if (performance.now() >= endTime || !char.service.device.gatt?.connected) {
                            throw err;
                        }
                        await delay(periodMsec);
                    }
                }
            }
        }
    }
    getChar(svc, char) {
        const svcRecord = this.svcs.get(svc);
        if (!svcRecord) {
            throw new BleDeviceError('charNotFound', `${svc}/${char}`);
        }
        if (!svcRecord.remote.device.gatt?.connected) {
            throw new BleDeviceError('notConnected', `${svc}/${char}`);
        }
        const charRecord = svcRecord.chars.get(char);
        if (!charRecord) {
            throw new BleDeviceError('charNotFound', `${svc}/${char}`);
        }
        return charRecord;
    }
    wrapSettlement(p) {
        return wrapSettlement(p, this.wrapper);
    }
    async startGatt() {
        if (!this.wbDev.gatt) {
            throw new Error('Could not get remote GATT server');
        }
        await this.wrapSettlement(this.wbDev.gatt.connect());
        this.logger.log('Connected to GATT server');
        const svcs = await this.wrapSettlement(this.wbDev.gatt.getPrimaryServices());
        for (const svc of svcs) {
            const chars = await this.wrapSettlement(svc.getCharacteristics());
            const charMap = new Map(chars.map((char) => [char.uuid, char]));
            this.svcs.set(svc.uuid, { remote: svc, chars: charMap });
        }
        this.logger.log('Discovered all services');
    }
}
//# sourceMappingURL=web-bluetooth-connected-device.js.map